/**
 * ReportDetailsContent - Shared report details display component
 *
 * Used in both ReportDetails (main app) and PopupReportPage for consistent display.
 * Shows all report information including status, schedule, shape, filter, etc.
 */
import React, { useMemo } from 'react';
import {
  LoadingSpinner,
  ScrollArea,
  Separator,
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Card,
} from '@ais-ui/ui-core';
import { ParsedReportParams, ReportTemplate, Job } from '@ais-ui/websockets';
import { useGetShiplist, useShape } from '@ais-ui/api';
import { Ship } from '@ais-ui/api-spec/config';
import { cn, isNonEmpty } from '@ais-ui/utils';
import { ReportStatusIcon } from './ReportStatusIcon';
import { getSelectLabelByValue, reportTypesObject } from './reportsTypes';
import { getFrontendColorScaleFromBackend } from './Mutations/utils/densityColorMapping';
import { PiCalendarBlank, PiMapPin, PiEye, PiEyeSlash } from 'react-icons/pi';
import { ScheduleDetails } from './ScheduleDetails';
import { useDateTimeFormat } from '../conversions/useDateFormat';

// ============================================================================
// Types
// ============================================================================

type LayoutVariant = 'sidebar' | 'popup' | 'popover';

interface ReportDetailsContentProps {
  report: ParsedReportParams | undefined;
  reportTemplate: ReportTemplate;
  job?: Job;
  /** Optional - if not provided, will use useDateTimeFormat hook internally */
  formatDateTimeWithTimeZone?: (date: Date | undefined | null) => string;
  onShowShape?: () => void;
  onShowFilter?: () => void;
  isShapeVisible?: boolean;
  isFilterVisible?: boolean;
  /** Layout variant - 'sidebar' for narrow layouts, 'popup' for wider popup windows */
  variant?: LayoutVariant;
  /** Pre-fetched shiplist data - if provided, skips internal data fetching */
  preloadedShiplist?: { name: string } | null;
  /** Pre-fetched shape data - if provided, skips internal data fetching */
  preloadedShape?: { properties: { name: string } } | null;
  /** Loading state for pre-fetched data */
  isLoadingPreloadedData?: boolean;
}

interface ReportDetailItemProps {
  label: string;
  value: string | undefined;
  className?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

const ReportDetailItem = ({
  label,
  value,
  className,
}: ReportDetailItemProps) => {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow truncate">
        {isNonEmpty(value) ? value : 'N/A'}
      </p>
    </div>
  );
};

const ReportDetailItemWithAction = ({
  label,
  value,
  icon,
  action,
  actionLabel,
  actionIcon,
  className,
}: ReportDetailItemProps & {
  icon?: React.ReactNode;
  action?: () => void;
  actionLabel?: string;
  actionIcon?: React.ReactNode;
}) => {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium flex-none flex items-center gap-1.5">
          {icon && <span className="text-muted-foreground">{icon}</span>}
          {label}
        </p>
        {action && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2"
                onClick={action}
              >
                {actionIcon}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{actionLabel}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <p className="text-sm text-muted-foreground flex-grow truncate">
        {isNonEmpty(value) ? value : 'N/A'}
      </p>
    </div>
  );
};

interface ShiplistReportDetailItemProps {
  label: string;
  shiplistId: string;
}

/**
 * ShipListReportDetailItem - Fetches and displays shiplist name
 * NOTE: This component fetches data internally. For popup windows, use
 * ShiplistReportDetailItemPure instead and pass pre-fetched data.
 */
const ShipListReportDetailItem = ({
  label,
  shiplistId,
}: ShiplistReportDetailItemProps) => {
  const {
    data: detailedShiplist,
    isLoading,
    isError,
  } = useGetShiplist(shiplistId);

  if (isError) {
    return null;
  }

  if (isLoading) {
    return <LoadingSpinner size={'small'} />;
  }

  if (!detailedShiplist) {
    return null;
  }

  return (
    <ReportDetailItem label={label} value={detailedShiplist?.name as string} />
  );
};

interface ShiplistReportDetailItemPureProps {
  label: string;
  /** Pre-fetched shiplist data */
  shiplist?: { name: string } | null;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * Pure presentation component for shiplist - no data fetching
 * Use this in popup windows where data comes from main window via BroadcastChannel
 */
const ShiplistReportDetailItemPure = ({
  label,
  shiplist,
  isLoading,
}: ShiplistReportDetailItemPureProps) => {
  if (isLoading) {
    return <LoadingSpinner size={'small'} />;
  }

  if (!shiplist) {
    return null;
  }

  return <ReportDetailItem label={label} value={shiplist.name} />;
};

interface ShipsReportDetailItemProps {
  label: string;
  ships: Ship[];
  className?: string;
}

const ShipsReportDetailItem = ({
  label,
  ships,
  className,
}: ShipsReportDetailItemProps) => {
  const formattedValue = ships.map((ship) => ship.name).join(', ');

  return (
    <ReportDetailItem
      className={className}
      label={label}
      value={formattedValue}
    />
  );
};

interface ShapeReportDetailItemProps {
  shapeId: string;
  onShowShape?: () => void;
  isShapeVisible?: boolean;
}

/**
 * ShapeReportDetailItem - Fetches and displays shape name
 * NOTE: This component fetches data internally. For popup windows, use
 * ShapeReportDetailItemPure instead and pass pre-fetched data.
 */
const ShapeReportDetailItem = ({
  shapeId,
  onShowShape,
  isShapeVisible,
}: ShapeReportDetailItemProps) => {
  const { data: shape, isLoading } = useShape(shapeId);

  const shapeName = useMemo(() => {
    if (shape) {
      return shape.properties.name;
    }
    return 'Instant or unknown shape';
  }, [shape]);

  if (isLoading) {
    return <LoadingSpinner size="small" />;
  }

  return (
    <ReportDetailItemWithAction
      label="Shape"
      value={shapeName}
      icon={<PiMapPin className="size-3.5" />}
      action={onShowShape}
      actionLabel={isShapeVisible ? 'Hide shape on map' : 'Show shape on map'}
      actionIcon={
        isShapeVisible ? (
          <PiEyeSlash className="size-3.5" />
        ) : (
          <PiEye className="size-3.5" />
        )
      }
    />
  );
};

interface ShapeReportDetailItemPureProps {
  onShowShape?: () => void;
  isShapeVisible?: boolean;
  /** Pre-fetched shape data */
  shape?: { properties: { name: string } } | null;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * Pure presentation component for shape - no data fetching
 * Use this in popup windows where data comes from main window via BroadcastChannel
 */
const ShapeReportDetailItemPure = ({
  onShowShape,
  isShapeVisible,
  shape,
  isLoading,
}: ShapeReportDetailItemPureProps) => {
  const shapeName = useMemo(() => {
    if (shape) {
      return shape.properties.name;
    }
    return 'Instant or unknown shape';
  }, [shape]);

  if (isLoading) {
    return <LoadingSpinner size="small" />;
  }

  return (
    <ReportDetailItemWithAction
      label="Shape"
      value={shapeName}
      icon={<PiMapPin className="size-3.5" />}
      action={onShowShape}
      actionLabel={isShapeVisible ? 'Hide shape on map' : 'Show shape on map'}
      actionIcon={
        isShapeVisible ? (
          <PiEyeSlash className="size-3.5" />
        ) : (
          <PiEye className="size-3.5" />
        )
      }
    />
  );
};

// ============================================================================
// Schedule Section - Uses the shared ScheduleDetails component
// ============================================================================

interface ScheduleSectionProps {
  reportTemplate: ReportTemplate;
}

const ScheduleSection = ({ reportTemplate }: ScheduleSectionProps) => {
  if (
    !reportTemplate.isScheduled ||
    reportTemplate.schedule?.interval === 'disabled'
  ) {
    return null;
  }

  return (
    <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
      <div className="flex items-center gap-2 px-4 pt-3">
        <PiCalendarBlank className="size-4 text-blue-600 dark:text-blue-400" />
        <h3 className="font-medium text-blue-700 dark:text-blue-300">
          Scheduled Report
        </h3>
        <Badge
          variant="secondary"
          className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
        >
          Active
        </Badge>
      </div>
      <ScheduleDetails reportTemplate={reportTemplate} />
    </Card>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const ReportDetailsContent = ({
  report,
  reportTemplate,
  job,
  formatDateTimeWithTimeZone: formatDateTimeWithTimeZoneProp,
  onShowShape,
  onShowFilter,
  isShapeVisible,
  isFilterVisible,
  variant = 'sidebar',
  preloadedShiplist,
  preloadedShape,
  isLoadingPreloadedData,
}: ReportDetailsContentProps) => {
  // Use provided formatter or fall back to hook
  const { formatDateTimeWithTimeZone: formatFromHook } = useDateTimeFormat();
  const formatDateTimeWithTimeZone =
    formatDateTimeWithTimeZoneProp || formatFromHook;

  const status = report?.info?.['Status'] || job?.status || '';
  const failureReason = job?.failureReason?.replace(': internal error', '');

  // Different grid classes based on variant
  // Sidebar/popover is narrow so we use fixed 2 columns
  // Popup has more space so we use responsive columns
  const gridClasses =
    variant === 'popup'
      ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'
      : 'grid grid-cols-2 gap-4';

  const headingSize =
    variant === 'popover'
      ? 'text-sm font-semibold text-primary'
      : 'text-lg font-medium';

  const content = (
    <div className="space-y-6">
      {/* Schedule Section - Show prominently at top if scheduled */}
      <ScheduleSection reportTemplate={reportTemplate} />

      {/* Basic Details */}
      <div className="grid gap-4">
        <div>
          <h3 className={headingSize}>Basic Details</h3>
          <Separator className="mt-2" />
        </div>
        <div className={gridClasses}>
          <ReportDetailItem
            label="Name"
            value={report?.info?.['Job name'] || job?.name}
          />
          <ReportDetailItem
            label="ID"
            value={report?.info?.['Job ID'] || job?.uuid}
          />
          <ReportDetailItem
            label="Type"
            value={report?.info?.['Job type'] || reportTemplate.type}
          />
          <ReportDetailItem
            label="Generated"
            value={formatDateTimeWithTimeZone(
              report?.info?.['Created'] || job?.creationdate,
            )}
          />
          <ReportDetailItem
            label="Created by"
            value={report?.info?.['Owner'] || job?.owner}
          />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium flex-none">Status</p>
            <div className="flex items-center gap-2">
              <ReportStatusIcon
                status={status}
                failureReason={failureReason}
                showLabel
                showTooltip={false}
              />
            </div>
          </div>
          {(report?.info?.Duration || job?.percentComplete !== undefined) && (
            <ReportDetailItem
              label="Duration"
              value={
                report?.info?.Duration ||
                (job?.percentComplete !== undefined
                  ? `${job.percentComplete}%`
                  : undefined)
              }
            />
          )}
        </div>
      </div>

      {/* Period */}
      <div className="grid gap-4">
        <div>
          <h3 className={headingSize}>Period</h3>
          <Separator className="mt-2" />
        </div>
        <div className={gridClasses}>
          <ReportDetailItem
            label="Period start time"
            value={formatDateTimeWithTimeZone(report?.info?.['Start time'])}
          />
          <ReportDetailItem
            label="Period end time"
            value={formatDateTimeWithTimeZone(report?.info?.['End time'])}
          />
        </div>
      </div>

      {/* Shape and Filter - Always show this section */}
      <div className="grid gap-4">
        <div>
          <h3 className={headingSize}>Shape and Filter</h3>
          <Separator className="mt-2" />
        </div>
        <div className={gridClasses}>
          {report?.info?.['Area'] ? (
            <ReportDetailItemWithAction
              label="Shape"
              value={report.info['Area']}
              icon={<PiMapPin className="size-3.5" />}
              action={onShowShape}
              actionLabel={
                isShapeVisible ? 'Hide shape on map' : 'Show shape on map'
              }
              actionIcon={
                isShapeVisible ? (
                  <PiEyeSlash className="size-3.5" />
                ) : (
                  <PiEye className="size-3.5" />
                )
              }
            />
          ) : report?.params?.['GAC']?.shape_id ? (
            // Use Pure version if preloaded data is provided (popup mode), otherwise fetch internally
            preloadedShape !== undefined ? (
              <ShapeReportDetailItemPure
                shape={preloadedShape}
                isLoading={isLoadingPreloadedData}
                onShowShape={onShowShape}
                isShapeVisible={isShapeVisible}
              />
            ) : (
              <ShapeReportDetailItem
                shapeId={report.params['GAC'].shape_id}
                onShowShape={onShowShape}
                isShapeVisible={isShapeVisible}
              />
            )
          ) : (
            <ReportDetailItem label="Shape" value="-" />
          )}
          {report?.params?.['departure']?.properties?.name && (
            <ReportDetailItem
              label="Departure"
              value={report.params['departure'].properties.name}
            />
          )}
          {report?.params?.['destination']?.properties?.name && (
            <ReportDetailItem
              label="Arrival"
              value={report.params['destination'].properties.name}
            />
          )}
          {report?.params?.['FN'] ? (
            <ReportDetailItemWithAction
              label="Filter"
              value={report.params['FN']}
              action={onShowFilter}
              actionLabel={
                isFilterVisible ? 'Hide filter' : 'Show filter details'
              }
              actionIcon={
                isFilterVisible ? (
                  <PiEyeSlash className="size-3.5" />
                ) : (
                  <PiEye className="size-3.5" />
                )
              }
            />
          ) : (
            <ReportDetailItem label="Filter" value="-" />
          )}
          {report?.params?.['TL'] && (
            <ShipsReportDetailItem
              className="col-span-2 md:col-span-3 lg:col-span-4"
              label="Vessels"
              ships={report.params['TL']}
            />
          )}
          {report?.params?.['shiplist'] &&
            // Use Pure version if preloaded data is provided (popup mode), otherwise fetch internally
            (preloadedShiplist !== undefined ? (
              <ShiplistReportDetailItemPure
                label="Shiplist"
                shiplist={preloadedShiplist}
                isLoading={isLoadingPreloadedData}
              />
            ) : (
              <ShipListReportDetailItem
                label="Shiplist"
                shiplistId={report.params['shiplist']}
              />
            ))}
        </div>
      </div>

      {/* Miscellaneous */}
      <MiscellaneousSection
        report={report}
        reportTemplate={reportTemplate}
        gridClasses={gridClasses}
        headingClassName={headingSize}
      />
    </div>
  );

  // Popover variant: no ScrollArea wrapper, just content with max-height overflow
  if (variant === 'popover') {
    return <div className="max-h-[60vh] overflow-y-auto p-0">{content}</div>;
  }

  return (
    <ScrollArea className="flex-grow p-0 overflow-y-auto">{content}</ScrollArea>
  );
};

// ============================================================================
// Miscellaneous Section
// ============================================================================

interface MiscellaneousSectionProps {
  report: ParsedReportParams | undefined;
  reportTemplate: ReportTemplate;
  gridClasses: string;
  headingClassName?: string;
}

interface MiscFieldDef {
  key: string;
  label: string | ((report: ParsedReportParams) => string);
  getValue: (
    report: ParsedReportParams,
    template: ReportTemplate,
  ) => string | undefined;
  condition?: (report: ParsedReportParams, template: ReportTemplate) => boolean;
  className?: string;
}

const MISC_PARAM_FIELDS: MiscFieldDef[] = [
  {
    key: 'CS',
    label: 'Color scale',
    getValue: (r) =>
      r.params?.['CS']
        ? getFrontendColorScaleFromBackend(r.params['CS'])
        : undefined,
  },
  {
    key: 'CB',
    label: 'Color by',
    getValue: (r, t) =>
      r.params?.['CB']
        ? getSelectLabelByValue(
            r.params['CB'],
            reportTypesObject[t.type]?.miscellaneous?.colorBy?.options || [],
          )
        : undefined,
    condition: (_, t) =>
      t.type === 'HISTORY_TRACK_TB' || t.type === 'DENSITY_TB',
  },
  {
    key: 'CD',
    label: 'Number of colors',
    getValue: (r) => (r.params?.['CD'] ? `${r.params['CD']}` : undefined),
  },
  {
    key: 'CPD',
    label: 'Cell size',
    getValue: (r) => (r.params?.['CPD'] ? `${r.params['CPD']}` : undefined),
  },
  {
    key: 'SMMLMIN',
    label: 'Lower limit',
    getValue: (r) =>
      r.params?.['SMMLMIN'] ? `${r.params['SMMLMIN']}` : undefined,
  },
  {
    key: 'SMMLMAX',
    label: 'Upper limit',
    getValue: (r) =>
      r.params?.['SMMLMAX'] ? `${r.params['SMMLMAX']}` : undefined,
  },
  {
    key: 'PAC',
    label: 'Output type',
    getValue: (r) => (r.params?.['PAC'] ? `${r.params['PAC']}` : undefined),
  },
  {
    key: 'STG',
    label: 'Statistic grouping',
    getValue: (r) => (r.params?.['STG'] ? `${r.params['STG']}` : undefined),
  },
  {
    key: 'PI',
    label: 'Interval',
    getValue: (r) => (r.params?.['PI'] ? `${r.params['PI']}` : undefined),
  },
];

const WATCHDOG_FIELDS: MiscFieldDef[] = Array.from({ length: 5 }, (_, i) => ({
  key: `Watchdog_${i + 1}`,
  label: (report: ParsedReportParams) => {
    if (i === 0) {
      const key2 = 'Watchdogs (2)' as string;
      return report.info?.[key2 as keyof typeof report.info]
        ? 'Watchdog 1'
        : 'Watchdog';
    }
    return `Watchdog ${i + 1}`;
  },
  getValue: (r: ParsedReportParams) => {
    const key = `Watchdogs (${i + 1})` as string;
    return r.info?.[key as keyof typeof r.info]
      ? `${r.info[key as keyof typeof r.info]}`
      : undefined;
  },
}));

const SQL_FIELD: MiscFieldDef = {
  key: 'SQL',
  label: 'SQL Query',
  getValue: (r) => r.params?.['SQL'] ?? undefined,
  condition: (_, t) => t.type === 'GENERIC_SQL',
  className: 'col-span-2',
};

const ALL_MISC_FIELDS = [...MISC_PARAM_FIELDS, ...WATCHDOG_FIELDS, SQL_FIELD];

const MiscellaneousSection = ({
  report,
  reportTemplate,
  gridClasses,
  headingClassName = 'text-lg font-medium',
}: MiscellaneousSectionProps) => {
  if (!report) return null;

  const visibleFields = ALL_MISC_FIELDS.filter((field) => {
    const value = field.getValue(report, reportTemplate);
    if (!value) return false;
    return !field.condition || field.condition(report, reportTemplate);
  });

  if (visibleFields.length === 0) return null;

  return (
    <div className="grid gap-4">
      <div>
        <h3 className={headingClassName}>Miscellaneous</h3>
        <Separator className="mt-2" />
      </div>
      <div className={gridClasses}>
        {visibleFields.map((field) => (
          <ReportDetailItem
            key={field.key}
            label={
              typeof field.label === 'function'
                ? field.label(report)
                : field.label
            }
            value={field.getValue(report, reportTemplate)!}
            className={field.className}
          />
        ))}
      </div>
    </div>
  );
};
